import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PaymentType, v4Service, ozowService, CreateSessionResponse, OzowEstimateQuoteResult } from "src/api";
import {
  onlyNumbers,
  formatNumberToExchange,
  generateDepositCallbackUrls,
  debounce,
  parseOzowEstimateQuoteError,
  _t,
} from "src/utils";
import { ERoutes } from "src/types";

const OZOW_ESTIMATE_QUOTE_DEBOUNCE_MS = 300;
const OZOW_ESTIMATE_QUOTE_REFRESH_MS = 30_000;

const parseQuoteExpiresAt = (expiresAt: string): number => {
  const normalizedExpiresAt = expiresAt.includes("T") ? expiresAt : expiresAt.replace(" ", "T");
  return new Date(normalizedExpiresAt).getTime();
};

interface UseDepositActionsParams {
  amount: string;
  setAmount: (amount: string) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSessionData: (data: CreateSessionResponse) => void;
  setOzowEstimateQuote: (quote: OzowEstimateQuoteResult | null) => void;
  setOzowEstimateQuoteLoading: (loading: boolean) => void;
  setOzowEstimateQuoteError: (error: string | null) => void;
  ozowEstimateQuoteExpiresAt: string | null;
  conversionFlowEnabled: boolean;
  amountPrecision: number;
  paymentRouteId: string | null;
  ozowEstimateQuote: OzowEstimateQuoteResult | null;
}

export const useDepositActions = (params: UseDepositActionsParams) => {
  const {
    amount,
    setAmount,
    setIsLoading,
    setError,
    setSessionData,
    setOzowEstimateQuote,
    setOzowEstimateQuoteLoading,
    setOzowEstimateQuoteError,
    ozowEstimateQuoteExpiresAt,
    conversionFlowEnabled,
    amountPrecision,
    paymentRouteId,
    ozowEstimateQuote,
  } = params;

  const navigate = useNavigate();

  const handleAmountChange = useCallback(
    (newAmount: string) => {
      if (onlyNumbers(newAmount)) {
        setAmount(formatNumberToExchange(newAmount, amountPrecision));
      }
    },
    [amountPrecision, setAmount]
  );

  const createSession = useCallback(
    async (sessionAmount: number) => {
      if (!paymentRouteId) {
        throw new Error(_t("ERRORS.PAYMENT_ROUTE_ID_REQUIRED"));
      }

      return await v4Service.createSession({
        data: {
          amount: sessionAmount,
          payment_route_id: paymentRouteId,
          type: PaymentType.Deposit,
          ...(conversionFlowEnabled ? { conversion: true } : {}),
        },
      });
    },
    [conversionFlowEnabled, paymentRouteId]
  );

  const requestOzowEstimateQuote = useCallback(
    async (quoteAmount: number) => {
      if (!conversionFlowEnabled) {
        return;
      }

      if (!paymentRouteId || quoteAmount <= 0) {
        setOzowEstimateQuote(null);
        setOzowEstimateQuoteError(null);
        setOzowEstimateQuoteLoading(false);
        return;
      }

      try {
        setOzowEstimateQuoteLoading(true);
        setOzowEstimateQuoteError(null);

        const quote = await v4Service.getOzowEstimateQuote({
          payment_route_id: paymentRouteId,
          amount: quoteAmount,
        });

        setOzowEstimateQuote(quote);
      } catch (error) {
        console.error("Error fetching Ozow estimate quote:", error);
        setOzowEstimateQuote(null);
        setOzowEstimateQuoteError(parseOzowEstimateQuoteError(error));
      } finally {
        setOzowEstimateQuoteLoading(false);
      }
    },
    [
      conversionFlowEnabled,
      paymentRouteId,
      setOzowEstimateQuote,
      setOzowEstimateQuoteError,
      setOzowEstimateQuoteLoading,
    ]
  );

  const debouncedRequestOzowEstimateQuote = useCallback(
    debounce(OZOW_ESTIMATE_QUOTE_DEBOUNCE_MS, (quoteAmount: number) => requestOzowEstimateQuote(quoteAmount)),
    [requestOzowEstimateQuote]
  );

  const initiateDeposit = useCallback(async ({ exchange, sessionId }: { exchange: string; sessionId: string }) => {
    const redirectUrls = generateDepositCallbackUrls();

    return await ozowService.initiateDeposit({
      exchange,
      session_id: sessionId,
      ...redirectUrls,
    });
  }, []);

  const handleInitiateDeposit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const sessionAmount = conversionFlowEnabled
        ? ozowEstimateQuote?.source_currency_amount
        : Number(amount);

      if (!sessionAmount || sessionAmount <= 0) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const sessionData = await createSession(sessionAmount);

        if (sessionData?.session_id && sessionData?.exchange) {
          setSessionData(sessionData);

          const ozowData = await initiateDeposit({
            exchange: sessionData.exchange,
            sessionId: sessionData.session_id,
          });

          if (ozowData?.redirectUrl) {
            navigate(`${ERoutes.PaymentRedirect}?redirectUrl=${encodeURIComponent(ozowData.redirectUrl)}`);
          }
        }
      } catch (error) {
        console.error("Error initiating deposit:", error);
        setError(_t("ERRORS.FAILED_TO_INITIATE_DEPOSIT"));
      } finally {
        setIsLoading(false);
      }
    },
    [
      amount,
      conversionFlowEnabled,
      ozowEstimateQuote,
      createSession,
      initiateDeposit,
      navigate,
      setError,
      setIsLoading,
      setSessionData,
    ]
  );

  useEffect(() => {
    if (!conversionFlowEnabled) {
      setOzowEstimateQuote(null);
      setOzowEstimateQuoteError(null);
      setOzowEstimateQuoteLoading(false);
      return;
    }

    debouncedRequestOzowEstimateQuote(Number(amount));
  }, [
    amount,
    conversionFlowEnabled,
    debouncedRequestOzowEstimateQuote,
    setOzowEstimateQuote,
    setOzowEstimateQuoteError,
    setOzowEstimateQuoteLoading,
  ]);

  useEffect(() => {
    if (!conversionFlowEnabled || !ozowEstimateQuoteExpiresAt || Number(amount) <= 0) {
      return;
    }

    const expiresAt = parseQuoteExpiresAt(ozowEstimateQuoteExpiresAt);

    if (Number.isNaN(expiresAt)) {
      return;
    }

    const delay = expiresAt - Date.now();
    const refreshDelay = delay > 0 ? delay : OZOW_ESTIMATE_QUOTE_REFRESH_MS;

    const timer = setTimeout(() => {
      requestOzowEstimateQuote(Number(amount));
    }, refreshDelay);

    return () => {
      clearTimeout(timer);
    };
  }, [amount, conversionFlowEnabled, ozowEstimateQuoteExpiresAt, requestOzowEstimateQuote]);

  return {
    handleAmountChange,
    handleInitiateDeposit,
  };
};
